import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtTL } from "@/components/StatusBadge";
import { toast } from "sonner";
import { Lightbulb, AlertTriangle, Check, Eye, Loader2, MessageSquare, Send } from "lucide-react";

const STATUS_TABS = [["new", "Yeni"], ["read", "Okundu"], ["resolved", "Çözüldü"], ["all", "Tümü"]];

export default function Feedback() {
  const [status, setStatus] = useState("new");
  const [type, setType] = useState("");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [replies, setReplies] = useState({});   // id -> text
  const [smsOpt, setSmsOpt] = useState({});      // id -> bool
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams();
    p.set("status", status);
    if (type) p.set("type", type);
    api.get(`/feedback?${p}`).then((r) => setList(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [status, type]);
  useEffect(() => { load(); }, [load]);

  const setSt = async (id, st) => {
    try {
      await api.post(`/feedback/${id}/status`, { status: st });
      toast.success(st === "resolved" ? "Çözüldü olarak işaretlendi" : st === "read" ? "Okundu olarak işaretlendi" : "Güncellendi");
      load();
    } catch (e) { toast.error("İşlem başarısız"); }
  };

  const sendReply = async (f, resolve) => {
    const reply = (replies[f.id] || "").trim();
    if (reply.length < 2) { toast.error("Yanıt metni girin"); return; }
    setBusy(f.id);
    try {
      const { data } = await api.post(`/feedback/${f.id}/reply`, { reply, resolve, send_sms: !!smsOpt[f.id] });
      toast.success(`Yanıt kaydedildi${data.sms_sent ? " · SMS gönderildi" : (smsOpt[f.id] ? " · SMS gönderilemedi (telefon/entegrasyon)" : "")}${resolve ? " · çözüldü" : ""}`);
      setReplies((m) => ({ ...m, [f.id]: "" }));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Yanıt gönderilemedi"); }
    finally { setBusy(""); }
  };

  return (
    <div className="space-y-4" data-testid="feedback-page">
      <div>
        <h1 className="font-heading text-2xl font-black text-slate-800">Öneri / Şikayet Penceresi</h1>
        <p className="text-sm text-slate-500">Müşterilerden gelen öneri ve şikayetler burada listelenir. Yanıt yazıp SMS ile iletebilir, çözüldü olarak kapatabilirsiniz.</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-md bg-slate-100 p-1">
          {STATUS_TABS.map(([id, label]) => (
            <button key={id} data-testid={`feedback-tab-${id}`} onClick={() => setStatus(id)} className={`rounded-sm px-3 py-1.5 text-sm font-semibold ${status === id ? "bg-white text-brand-green shadow-sm" : "text-slate-500"}`}>{label}</button>
          ))}
        </div>
        <select data-testid="feedback-filter-type" value={type} onChange={(e) => setType(e.target.value)} className="h-9 rounded-md border border-input bg-white px-2 text-sm">
          <option value="">Tüm türler</option><option value="suggestion">Öneri</option><option value="complaint">Şikayet</option>
        </select>
      </div>
      {loading ? <div className="p-10 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-brand-green" /></div> : (
        <div className="grid gap-3 md:grid-cols-2">
          {list.length === 0 && <p className="col-span-full py-10 text-center text-slate-400">Kayıt yok</p>}
          {list.map((f) => (
            <div key={f.id} data-testid={`feedback-row-${f.id}`} className={`rounded-lg bg-white p-4 ring-1 ring-inset ${f.type === "complaint" ? "ring-red-100" : "ring-emerald-100"}`}>
              <div className="mb-2 flex items-center justify-between">
                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${f.type === "complaint" ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-700"}`}>
                  {f.type === "complaint" ? <AlertTriangle className="h-3 w-3" /> : <Lightbulb className="h-3 w-3" />} {f.type === "complaint" ? "Şikayet" : "Öneri"}
                </span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${f.status === "new" ? "bg-brand-orange/10 text-brand-orange" : f.status === "resolved" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{f.status === "new" ? "YENİ" : f.status === "resolved" ? "ÇÖZÜLDÜ" : "OKUNDU"}</span>
              </div>
              <p className="text-sm text-slate-700">{f.message}</p>
              <div className="mt-2 space-y-0.5 text-[11px] text-slate-400">
                <p>{f.name || "Anonim"}{f.phone ? ` · ${f.phone}` : ""}</p>
                {f.sale_no && <p>Fiş: <span className="font-mono">{f.sale_no}</span>{f.total != null ? ` · ${fmtTL(f.total)}` : ""}{f.location_name ? ` · ${f.location_name}` : ""}</p>}
                <p>{f.created_at ? new Date(f.created_at).toLocaleString("tr-TR") : ""}</p>
              </div>

              {f.reply && (
                <div data-testid={`feedback-reply-${f.id}`} className="mt-3 rounded-md bg-brand-green/5 p-2.5 ring-1 ring-inset ring-brand-green/20">
                  <p className="mb-0.5 flex items-center gap-1 text-[11px] font-semibold text-brand-green"><MessageSquare className="h-3 w-3" /> Yönetici yanıtı{f.reply_sms_sent ? " · SMS gönderildi" : ""}</p>
                  <p className="text-sm text-slate-700">{f.reply}</p>
                  <p className="mt-1 text-[10px] text-slate-400">{f.replied_by}{f.replied_at ? ` · ${new Date(f.replied_at).toLocaleString("tr-TR")}` : ""}</p>
                </div>
              )}

              {f.status !== "resolved" && (
                <div className="mt-3 border-t border-slate-100 pt-3">
                  <textarea data-testid={`feedback-reply-input-${f.id}`} value={replies[f.id] || ""} onChange={(e) => setReplies((m) => ({ ...m, [f.id]: e.target.value }))}
                    rows={2} placeholder={f.reply ? "Yeni yanıt / güncelleme…" : "Müşteriye yanıt yazın…"} className="w-full rounded-md border border-input p-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green" />
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {f.phone && (
                      <label className="inline-flex items-center gap-1.5 text-xs text-slate-600">
                        <input data-testid={`feedback-reply-sms-${f.id}`} type="checkbox" checked={!!smsOpt[f.id]} onChange={(e) => setSmsOpt((m) => ({ ...m, [f.id]: e.target.checked }))} className="h-4 w-4 rounded border-slate-300 text-brand-green" />
                        SMS ile gönder
                      </label>
                    )}
                    <div className="ml-auto flex gap-2">
                      <button data-testid={`feedback-reply-save-${f.id}`} disabled={busy === f.id} onClick={() => sendReply(f, false)} className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-200 disabled:opacity-50"><Send className="h-3.5 w-3.5" /> Yanıtı Kaydet</button>
                      <button data-testid={`feedback-reply-resolve-${f.id}`} disabled={busy === f.id} onClick={() => sendReply(f, true)} className="inline-flex items-center gap-1 rounded-md bg-brand-green px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-green-600 disabled:opacity-50"><Check className="h-3.5 w-3.5" /> Yanıtla &amp; Çözüldü</button>
                    </div>
                  </div>
                  <div className="mt-2 flex gap-2">
                    {f.status === "new" && <button data-testid={`feedback-read-${f.id}`} onClick={() => setSt(f.id, "read")} className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-slate-700"><Eye className="h-3.5 w-3.5" /> Yanıtsız okundu işaretle</button>}
                    <button data-testid={`feedback-resolve-${f.id}`} onClick={() => setSt(f.id, "resolved")} className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600 hover:text-emerald-700"><Check className="h-3.5 w-3.5" /> Yanıtsız çözüldü işaretle</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
